import { AfterViewInit, Component, EventEmitter, Output, QueryList, ViewChildren } from '@angular/core';
import { AnalysisType, CopilotService, IAnalysisFinding } from '../../services/copilot.service';
import { catchError, throwError } from 'rxjs';

export interface IAnalysisTab {
  isActive: boolean;
  title: string;
};

@Component({
    selector: 'app-copilot-analysis',
    templateUrl: './copilot-analysis.component.html',
    styleUrls: ['./copilot-analysis.component.scss'],
    standalone: false
})
export class CopilotAnalysisComponent implements AfterViewInit {
  static DEFAULT_START_COLUMN = 1;
  static DEFAULT_END_COLUMN = 1;

  @ViewChildren('spinner') allSpinners: QueryList<any>;

  @Output()
  findingSelected = new EventEmitter<IAnalysisFinding>();

  @Output()
  clearSelected = new EventEmitter<any>();

  protected analyzing: boolean = false;
  protected copilotFindings: Map<AnalysisType, IAnalysisFinding[]> =
    new Map<AnalysisType, IAnalysisFinding[]>();

  tabs: Map<AnalysisType, IAnalysisTab> = new Map<AnalysisType, IAnalysisTab>([
    [AnalysisType.Logic, { isActive: false, title: "Code" }],
    [AnalysisType.Performance, <IAnalysisTab>({ isActive: false, title: "Performance" })],
    [AnalysisType.Security, <IAnalysisTab>({ isActive: false, title: "Security" })],
    [AnalysisType.BestPractices, <IAnalysisTab>({ isActive: false, title: "Best Practices" })]
  ]);

  constructor(protected copilotService: CopilotService) {
  }

  ngAfterViewInit() {
    setTimeout(() => this.ensureFirstTabIsActive(), 100);

    this.allSpinners.changes.subscribe(s => {
      setTimeout(() => this.ensureFirstTabIsActive(), 100);
    });
  }

  hasFindings(key: AnalysisType): boolean {
    return this.copilotFindings.get(key)?.length > 0;
  }

  findingClassName(finding: IAnalysisFinding) {
    return `${finding.selected ?
      'datex-copilot-selected-finding' : ''} ${finding.completed ?
        'datex-copilot-finding-checked' : ''}`;
  }

  severityClassName(finding: IAnalysisFinding) {
    if (finding.completed || (finding.selected && finding.hovering)) {
      return `checkmark_20_regular ${finding.completed ? "datex-copilot-icon-checked" : "datex-copilot-icon-checkable"}`;
    }

    switch (finding.severity) {
      case 1:
        return "error_circle_20_regular datex-copilot-icon-error";
      case 2:
        return "warning_20_regular datex-copilot-icon-warning";
      default:
        return "info_20_regular datex-copilot-icon-info";
    }
  }

  onMouseOver(finding: IAnalysisFinding) {
    finding.hovering = true;
  }

  onMouseOut(finding: IAnalysisFinding) {
    finding.hovering = false;
  }

  protected onFindingSelected(finding: IAnalysisFinding) {
    this.findingSelected.emit(finding);
  }

  protected clearCodeHighlighting() {
    this.clearSelected.emit();
  }

  unselectAllFindings() {
    this.copilotFindings.forEach((findings: IAnalysisFinding[]) =>
      findings.forEach((finding: IAnalysisFinding) => finding.selected = false));
  }

  findingsBadgeCount(key: AnalysisType): number {
    var count = 0;
    this.copilotFindings.get(key).forEach(finding => count += finding.completed ? 0 : 1);
    return count;
  }

  hasUncompletedFindings(key: AnalysisType): boolean {
    return this.findingsBadgeCount(key) > 0;
  }

  titleClassName(key: AnalysisType, value: IAnalysisTab): string {
    return value.isActive ? 'datex-copilot-title-active' : 'datex-copilot-title';
  }

  highestSeverity(findings: IAnalysisFinding[]): number {
    var severity = 5;
    findings.forEach(finding => severity = !finding.completed && finding.severity < severity ? finding.severity : severity);
    return severity;
  }

  badgeClassName(key: AnalysisType): string {
    var severity = this.highestSeverity(this.copilotFindings.get(key));
    return `datex-copilot-badge datex-copilot-sev-${severity === 1 ? 'high' : severity === 2 ? 'medium' : 'low'}`;
  }

  onSelectFinding(finding: IAnalysisFinding) {
    if (finding.completed) {
      this.unselectAllFindings();
      finding.selected = true;
      finding.completed = false;
      this.onFindingSelected(finding);
      return;
    }

    if (finding.selected) {
      finding.selected = false;
      finding.completed = true;
      this.clearCodeHighlighting();
      return;
    }

    this.unselectAllFindings();
    finding.selected = true;
    this.onFindingSelected(finding);
  }

  onActivateClicked(key: AnalysisType, value: IAnalysisTab) {
    if (this.analyzing && !this.hasFindings(key)) { return };

    this.unselectAllFindings();
    this.resetTabs();
    value.isActive = true;
    this.clearCodeHighlighting();
  }

  ensureFirstTabIsActive() {
    var hasActive = false;

    if (!this.copilotFindings.size) {
      return;
    }

    this.tabs.forEach(tab => { hasActive = hasActive ? true : tab.isActive; });
    if (hasActive) { return; };

    this.tabs.forEach((value: IAnalysisTab, key: AnalysisType) => {
      if (hasActive) { return; };
      if (this.copilotFindings.get(key).length) {
        value.isActive = true;
        hasActive = true;
      }
    });
  }

  resetTabs() {
    this.tabs.forEach(tab => tab.isActive = false);
  }


  public sortFindings(findings: IAnalysisFinding[]): IAnalysisFinding[] {
    findings.sort((left, right) =>
      left.severity - right.severity ||
      this.firstLineNumber(left.lineNumber) - this.firstLineNumber(right.lineNumber));

    return findings;
  }

  private firstLineNumber(lineNumber: string): number {

    if (lineNumber.includes(',')) {
      lineNumber = lineNumber.split(',')[0];
    } else if (lineNumber.includes('&')) {
      lineNumber = lineNumber.split('&')[0];
    }

    if (lineNumber.includes('-')) {
      lineNumber = lineNumber.split('-')[0];
    }

    return parseInt(lineNumber);
  }

  public async analyzeCode(code: string) {
    try {
      this.analyzing = true;

      this.copilotFindings.set(AnalysisType.Logic, []);
      this.copilotFindings.set(AnalysisType.Performance, []);
      this.copilotFindings.set(AnalysisType.Security, []);
      this.copilotFindings.set(AnalysisType.BestPractices, []);

      this.clearCodeHighlighting();
      this.resetTabs();

      await Promise.all([
        this.analyzeFlow(AnalysisType.Performance, code),
        this.analyzeFlow(AnalysisType.Security, code),
        this.analyzeFlow(AnalysisType.BestPractices, code),
        this.analyzeFlow(AnalysisType.Logic, code)
      ]);

    } finally {
      this.analyzing = false;
    }
  }

  protected analyzeFlow(type: AnalysisType, value: string): Promise<any> {
    return new Promise((resolve) => {
      this.copilotService.analyzeFlow(type, value)
        .pipe(catchError(e => {
          resolve(false);
          return throwError(() => e);
        }))
        .subscribe((results: any) => {
          this.copilotFindings.set(type, this.sortFindings(results.findings));
          resolve(true);
        });
    });
  }
}
